import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { CheckCircle, Loader2, Mail, Send, Settings } from 'lucide-react';

export default function BackupSchedulerConfig({
  schedulerConfig, setSchedulerConfig,
  newEmail, setNewEmail,
  savingConfig, saveSchedulerConfig,
  testingNotification, testNotification,
  addEmail, removeEmail
}) {
  return (
      <Card className="border-2 border-purple-200">
        <CardHeader className="bg-purple-50">
          <CardTitle className="text-lg flex items-center gap-2">
            <Settings className="h-5 w-5 text-purple-600" />
            Configuration Backup Automatique
          </CardTitle>
          <CardDescription>
            Programmez des backups automatiques avec notifications email
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6 space-y-6">
          {/* Activation */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-base font-semibold">Activer le backup automatique</Label>
              <p className="text-sm text-gray-500 mt-1">
                Exécute un backup tous les jours à l'heure programmée
              </p>
            </div>
            <Switch
              checked={schedulerConfig.enabled}
              onCheckedChange={(checked) => setSchedulerConfig(prev => ({ ...prev, enabled: checked }))}
            />
          </div>

          {/* Heure de programmation */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="hour">Heure</Label>
              <Input
                id="hour"
                type="number"
                min="0"
                max="23"
                value={schedulerConfig.schedule_hour}
                onChange={(e) => setSchedulerConfig(prev => ({ 
                  ...prev, 
                  schedule_hour: parseInt(e.target.value) || 0 
                }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="minute">Minute</Label>
              <Input
                id="minute"
                type="number"
                min="0"
                max="59"
                value={schedulerConfig.schedule_minute}
                onChange={(e) => setSchedulerConfig(prev => ({ 
                  ...prev, 
                  schedule_minute: parseInt(e.target.value) || 0 
                }))}
                className="mt-1"
              />
            </div>
          </div>

          {/* Emails de notification */}
          <div>
            <Label className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4" />
              Emails de notification (en cas d'échec)
            </Label>
            <div className="flex gap-2 mb-2">
              <Input
                type="email"
                placeholder="email@exemple.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addEmail()}
              />
              <Button onClick={addEmail} variant="outline">
                Ajouter
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {schedulerConfig.notification_emails.map((email) => (
                <Badge 
                  key={email} 
                  className="bg-purple-700 text-white hover:bg-purple-200 cursor-pointer font-semibold"
                  onClick={() => removeEmail(email)}
                >
                  {email} ✕
                </Badge>
              ))}
              {schedulerConfig.notification_emails.length === 0 && (
                <span className="text-sm text-gray-500 italic">
                  Aucun email configuré - les notifications seront désactivées
                </span>
              )}
            </div>
          </div>

          {/* Notifier en cas de succès */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <Label className="text-sm font-medium">Notifier aussi en cas de succès</Label>
              <p className="text-xs text-gray-500 mt-1">
                Recevoir un email de confirmation après chaque backup réussi
              </p>
            </div>
            <Switch
              checked={schedulerConfig.notify_on_success}
              onCheckedChange={(checked) => setSchedulerConfig(prev => ({ ...prev, notify_on_success: checked }))}
            />
          </div>

          {/* Boutons */}
          <div className="flex flex-wrap gap-3 pt-4 border-t">
            <Button 
              onClick={saveSchedulerConfig}
              disabled={savingConfig}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {savingConfig ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle className="h-4 w-4 mr-2" />
              )}
              Sauvegarder la configuration
            </Button>

            <Button 
              onClick={testNotification}
              disabled={testingNotification || schedulerConfig.notification_emails.length === 0}
              variant="outline"
            >
              {testingNotification ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              Tester les notifications
            </Button>
          </div>
        </CardContent>
      </Card>
  );
}
